import {
  ArrowClockwise,
  CheckSquareOffset,
  Info,
  Warehouse,
} from '@phosphor-icons/react';
import styled from 'styled-components';
import { TableCell, TableRow } from 'semantic-ui-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Popover, Tag, Tooltip } from 'antd';
import { ActionEditDots, ActionMenuItem, ItemImage } from '../../components';
import { CommonUtility } from '../../utility/common';
import { BoldText, ColorText, FlexRow, MuteText, StyledCheckbox } from '../../elements';
import { DateFormat, DateUtility } from '../../utility/date';
import { OrganisationAvatar } from '../../components/ImageAvatar';
import { theme } from '../../utility/theme';

const InfoBoxContainer = styled.div`
  color: ${({ theme }) => theme.colors.black};
  width: 500px;
  max-height: 400px;
  overflow: auto;
  font-size: 12px;
  line-height: normal;
  letter-spacing: normal;

  img {
    width: 33px !important;
    height: 33px !important;
  }

  ${FlexRow} {
    padding: 0 10px;
  }

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    word-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    margin: 0;
  }
`;

const ActionEditTableRow = styled(TableRow)`
  position: relative;

  .action-container {
    margin: auto;
    background: transparent;
    right: 10px;
    bottom: 0;
  }

  &:hover {
    img.m-auto {
      opacity: 0.3;
    }
    .action-container {
      opacity: 1 !important;
    }
  }
`;

function ItemBox({ items, showPrice }) {
  return (
    <InfoBoxContainer>
      <div className="row mx-0 mb-2 align-items-center">
        <div className="col-8 px-0">
          <MuteText>Product Information</MuteText>
        </div>
        <div className="col-2 text-center px-0">
          <MuteText>Quantity</MuteText>
        </div>
        {showPrice && (
          <div className="col-2 text-center px-0">
            <MuteText>Total</MuteText>
          </div>
        )}
      </div>
      {(items || []).map(item => (
        <div key={item.item_id} className="row mx-0 mb-2 align-items-center">
          <div className="col-2 px-0">
            <ItemImage size="small" item={item} extraClasses="m-0" />
          </div>
          <div className="col-6">
            <div>
              <span>{item.title}</span>
            </div>
            <div>
              <BoldText>{item.sku}</BoldText>
            </div>
          </div>
          <div className="col-2 text-center px-0">
            {item.available ? (
              <span>x{item.qty}</span>
            ) : (
              <span>x{CommonUtility.numberWithCommas(item.qty)}</span>
            )}
          </div>
          {showPrice && (
            <div className="col-2 text-center px-0">
              <span>{CommonUtility.currencyFormat(item.itemPrice)}</span>
            </div>
          )}
        </div>
      ))}
    </InfoBoxContainer>
  );
}

const Trigger = styled.div`
  max-width: fit-content;
`;

function getPopoverPlacement(position) {
  if (position === 'top left') return 'topLeft';
  if (position === 'top center') return 'top';
  if (position === 'top right') return 'topRight';
  if (position === 'bottom left') return 'bottomLeft';
  if (position === 'bottom center') return 'bottom';
  if (position === 'bottom right') return 'bottomRight';
  if (position === 'left center') return 'left';
  if (position === 'right center') return 'right';
  return position || 'topLeft';
}

export function ItemInfoPopup({ total_items, items, position, showPrice = true }) {
  return (
    <>
      <Popover
        content={<ItemBox items={items} showPrice={showPrice} />}
        placement={getPopoverPlacement(position)}
      >
        <Trigger
          className="d-flex align-items-center justify-content-center cursor-pointer"
          onClick={e => e.stopPropagation()}
        >
          {CommonUtility.numberWithCommas(items.length)} item(s)
          <Info className="ml-1" />
        </Trigger>
      </Popover>
      <Trigger>
        <MuteText>({CommonUtility.numberWithCommas(total_items)} unit(s))</MuteText>
      </Trigger>
    </>
  );
}

export const NameContainer = styled.div`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PauseReason = styled.div`
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export function getPauseReasonLabel(reason) {
  if (reason === 'non_pickable_allocations') {
    return 'Inventory allocated in non-picking location';
  }
  return reason || 'Fulfillment paused';
}

export const pendingIssueTypes = {
  nonPickLocation: 'non_pick_location',
  awaitingWaveEnqueue: 'awaiting_wave_enqueue',
};

export function getPendingFulfillmentItems(item) {
  return Array.isArray(item?.wms_fulfillment_pending) ? item.wms_fulfillment_pending : [];
}

function formatPendingItem(item) {
  const product = item?.sku || item?.title || item?.itemId || item?.productId;
  const location = item?.locationName || item?.locationCode;
  const quantity = item?.quantity ?? item?.qty;
  const quantityLabel =
    quantity === undefined || quantity === null
      ? ''
      : ` x${CommonUtility.numberWithCommas(quantity)}`;
  if (product && location) return `${product} at ${location}${quantityLabel}`;
  if (product || location) return `${product || location}${quantityLabel}`;
  return null;
}

export function PendingFulfillmentTooltip({ message, items }) {
  return (
    <div>
      <div>{message}</div>
      {items?.length ? (
        <div className="mt-1">
          <div>Items:</div>
          <ul className="mb-0 pl-3">
            {items.map((item, index) => {
              const label = formatPendingItem(item);
              return label ? <li key={item?.allocationId || index}>{label}</li> : null;
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function OrderTime({ time, isEnabled }) {
  let timeColor = '';
  const noOfDays = DateUtility.noOfDays(time);
  if (isEnabled) {
    if (noOfDays < 1) {
      timeColor = theme.colors.green;
    } else if (noOfDays <= 2) {
      timeColor = theme.colors.yellow;
    } else if (noOfDays <= 5) {
      timeColor = theme.colors.orange;
    } else if (noOfDays > 5) {
      timeColor = theme.colors.danger;
    }
  }
  return (
    <ColorText color={timeColor}>
      <b>{DateUtility.hourMinuteTime(time)}</b>
    </ColorText>
  );
}

export function OrderRow({ item, openPopup, showAction }) {
  const totalItems = useMemo(() => {
    let qty = 0;
    item.items.forEach(item => {
      qty += item.qty;
    });
    return qty;
  }, [item]);

  const provider = useMemo(() => item?.items?.[0]?.providerDetails?.[0], [item]);

  return (
    <ActionEditTableRow>
      <TableCell>
        <div className="d-flex align-items-start">
          <div>
            {provider ? (
              <Link
                to={`/app/fulfill/orders/details/${item._id}/?providerId=${provider?._id}`}
              >
                {item.marketplaceOrderDisplayId}
              </Link>
            ) : (
              item.marketplaceOrderDisplayId
            )}
          </div>
        </div>
      </TableCell>
      <TableCell textAlign="left">
        {item.timeAcknowledged && (
          <>
            <OrderTime time={item.timeAcknowledged} isEnabled />
            <div>
              <MuteText>
                {DateUtility.formatDate(item.timeAcknowledged, DateFormat.barChartMonth)}
              </MuteText>
            </div>
          </>
        )}
      </TableCell>
      <TableCell textAlign="left">
        {provider?.warehouseName && (
          <div className="d-flex justify-content-start align-items-center">
            <Warehouse size={20} className="mr-2" color={theme.colors.grey} />
            <NameContainer>{provider?.warehouseName}</NameContainer>
          </div>
        )}
      </TableCell>
      <TableCell textAlign="left">
        <div className="d-flex justify-content-start align-items-center">
          <OrganisationAvatar image={item.logo} name={item.organizationName} />
          <NameContainer>&nbsp;{item.organizationName}</NameContainer>
        </div>
      </TableCell>
      <TableCell textAlign="left">
        <div className="d-flex justify-content-start align-items-center">
          &nbsp;{item.shippingAddress.name || <MuteText>N/A</MuteText>}
        </div>
      </TableCell>

      <TableCell textAlign="center">
        <div className="d-flex justify-content-center align-items-center flex-column">
          <ItemInfoPopup
            items={item.items}
            position="top left"
            total_items={CommonUtility.numberWithCommas(totalItems)}
          />
        </div>
        {showAction && (
          <ActionEditDots preventChangingBg on="hover" position="left center">
            <ActionMenuItem
              onClick={() => {
                openPopup(item);
              }}
            >
              <CheckSquareOffset />
              Fulfill
            </ActionMenuItem>
          </ActionEditDots>
        )}
      </TableCell>
    </ActionEditTableRow>
  );
}

export function PausedOrderRow({
  item,
  selectedById = {},
  onBulkSelectToggle,
  onRecalculatePaused,
  recalculatingOrderId = null,
  recalculatingPaused = false,
}) {
  const totalItems = useMemo(() => {
    let qty = 0;
    item.items.forEach(item => {
      qty += item.qty;
    });
    return qty;
  }, [item]);

  const provider = useMemo(() => item?.items?.[0]?.providerDetails?.[0], [item]);

  const pendingItems = getPendingFulfillmentItems(item);
  const nonPickLocationItems = pendingItems.filter(
    item => item?.issue === pendingIssueTypes.nonPickLocation,
  );
  const isAwaitingWaveEnqueue =
    pendingItems.length > 0 &&
    pendingItems.every(item => item?.issue === pendingIssueTypes.awaitingWaveEnqueue);
  const pauseReason = getPauseReasonLabel(item?.wms_fulfillment_pause?.reason);
  const bulkSelected = !!selectedById[item._id];
  const isRowRecalculating = recalculatingPaused || recalculatingOrderId === item._id;

  let tagColor = 'default';
  let tagMessage = pauseReason;
  let tooltipItems = pendingItems;
  let tooltipMessage = pauseReason;

  if (nonPickLocationItems.length) {
    const itemCount = CommonUtility.numberWithCommas(nonPickLocationItems.length);
    tagColor = 'warning';
    tagMessage = `Paused: ${itemCount} item(s) in non-pick location`;
    tooltipItems = nonPickLocationItems;
    tooltipMessage =
      'This order is paused because these items are in non-pick locations.';
  } else if (isAwaitingWaveEnqueue) {
    tagColor = 'success';
    tagMessage = 'Ready: waiting for wave enqueue';
    tooltipMessage =
      'This order is waiting for wave enqueue. Select it and press resume.';
  }

  return (
    <ActionEditTableRow>
      <TableCell>
        <div className="d-flex align-items-start">
          <StyledCheckbox
            label=" "
            checked={bulkSelected}
            disabled={isRowRecalculating}
            onChange={(_, { checked }) => onBulkSelectToggle?.(item, Boolean(checked))}
            className="mr-2 mt-1"
          />
          <div>
            {provider ? (
              <Link
                to={`/app/fulfill/orders/details/${item._id}/?providerId=${provider?._id}`}
              >
                {item.marketplaceOrderDisplayId}
              </Link>
            ) : (
              item.marketplaceOrderDisplayId
            )}
            <PauseReason>
              <Tooltip
                title={
                  <PendingFulfillmentTooltip
                    message={tooltipMessage}
                    items={tooltipItems}
                  />
                }
              >
                <Tag color={tagColor}>{tagMessage}</Tag>
              </Tooltip>
            </PauseReason>
          </div>
        </div>
      </TableCell>
      <TableCell textAlign="left">
        {item.timeAcknowledged && (
          <>
            <OrderTime time={item.timeAcknowledged} isEnabled />
            <div>
              <MuteText>
                {DateUtility.formatDate(item.timeAcknowledged, DateFormat.barChartMonth)}
              </MuteText>
            </div>
          </>
        )}
      </TableCell>
      <TableCell textAlign="left">
        {provider?.warehouseName && (
          <div className="d-flex justify-content-start align-items-center">
            <Warehouse size={20} className="mr-2" color={theme.colors.grey} />
            <NameContainer>{provider?.warehouseName}</NameContainer>
          </div>
        )}
      </TableCell>
      <TableCell textAlign="left">
        <div className="d-flex justify-content-start align-items-center">
          <OrganisationAvatar image={item.logo} name={item.organizationName} />
          <NameContainer>&nbsp;{item.organizationName}</NameContainer>
        </div>
      </TableCell>
      <TableCell textAlign="left">
        <div className="d-flex justify-content-start align-items-center">
          &nbsp;{item.shippingAddress.name || <MuteText>N/A</MuteText>}
        </div>
      </TableCell>

      <TableCell textAlign="center">
        <div className="d-flex justify-content-center align-items-center flex-column">
          <ItemInfoPopup
            items={item.items}
            position="top left"
            total_items={CommonUtility.numberWithCommas(totalItems)}
          />
        </div>
        <ActionEditDots preventChangingBg on="hover" position="left center">
          <ActionMenuItem
            disabled={isRowRecalculating}
            onClick={() => {
              onRecalculatePaused?.(item._id);
            }}
          >
            <ArrowClockwise />
            {isRowRecalculating ? 'Recalculating...' : 'Recalculate Fulfillment'}
          </ActionMenuItem>
        </ActionEditDots>
      </TableCell>
    </ActionEditTableRow>
  );
}
